import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × SEURAT: Pointillism becomes pixels
// Dot grids, color quantization, visual sampling
export function VasarelySeuratMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Atmospheric pointillist background */}
      <rect x="0" y="0" width="794" height="1123" fill={brand.deep} />

      <g transform="translate(397 540)">
        {/* Dense pointillist field */}
        {Array.from({ length: 60 }).map((_, i) =>
          Array.from({ length: 80 }).map((_, j) => {
            const x = (i - 29.5) * 12;
            const y = (j - 39.5) * 12;
            const dist = Math.sqrt(x * x + y * y);
            const pattern = Math.sin(dist * 0.05 + i * 0.2 + j * 0.2);
            const colors = [brand.aqua, brand.violet, brand.amber, brand.white];
            const colorIndex = Math.floor((pattern + 1) * 2) % 4;
            const opacity = 0.3 + pattern * 0.2;
            
            return (
              <circle
                key={`${i}-${j}`}
                cx={x}
                cy={y}
                r="3"
                fill={colors[colorIndex]}
                opacity={opacity}
              />
            );
          })
        )}

        {/* Color quantization circles */}
        {Array.from({ length: 15 }).map((_, i) => {
          const r = 50 + i * 18;
          const colors = [brand.aqua, brand.violet, brand.amber];
          return (
            <circle
              key={i}
              cx="0"
              cy="0"
              r={r}
              fill="none"
              stroke={colors[i % 3]}
              strokeWidth="1"
              opacity={0.2}
              strokeDasharray="3 3"
            />
          );
        })}

        {/* Optical sampling grid */}
        {Array.from({ length: 12 }).map((_, i) =>
          Array.from({ length: 16 }).map((_, j) => {
            const x = (i - 5.5) * 48;
            const y = (j - 7.5) * 44;
            const sample = Math.sin(i * 0.6) * Math.cos(j * 0.5);
            
            if (Math.abs(sample) > 0.3) {
              return (
                <g key={`${i}-${j}`}>
                  <circle cx={x - 6} cy={y - 6} r="2.5" fill={brand.aqua} opacity="0.5" />
                  <circle cx={x + 6} cy={y - 6} r="2.5" fill={brand.violet} opacity="0.5" />
                  <circle cx={x - 6} cy={y + 6} r="2.5" fill={brand.amber} opacity="0.5" />
                  <circle cx={x + 6} cy={y + 6} r="2.5" fill={brand.white} opacity="0.5" />
                </g>
              );
            }
            return null;
          })
        )}

        {/* Pointillist figures (data shapes) */}
        {[
          { cx: -120, cy: -100, rx: 50, ry: 80 },
          { cx: 130, cy: -90, rx: 55, ry: 75 },
          { cx: -100, cy: 110, rx: 60, ry: 70 },
          { cx: 120, cy: 120, rx: 52, ry: 78 },
        ].map((shape, i) => (
          <g key={i}>
            {Array.from({ length: 80 }).map((_, j) => {
              const angle = (j / 80) * Math.PI * 2;
              const r = 0.9 + Math.random() * 0.2;
              const x = shape.cx + Math.cos(angle) * shape.rx * r;
              const y = shape.cy + Math.sin(angle) * shape.ry * r;
              const colors = [brand.aqua, brand.violet, brand.amber];
              
              return (
                <circle
                  key={j}
                  cx={x}
                  cy={y}
                  r="2.5"
                  fill={colors[j % 3]}
                  opacity="0.6"
                />
              );
            })}
          </g>
        ))}

        {/* Scientific chromatic separation */}
        {[-180, -60, 60, 180].map((x) => (
          <line
            key={x}
            x1={x}
            y1="-280"
            x2={x}
            y2="280"
            stroke={brand.white}
            strokeWidth="0.5"
            opacity="0.15"
            strokeDasharray="2 2"
          />
        ))}

        {/* Central optical focus */}
        {Array.from({ length: 30 }).map((_, i) => {
          const angle = (i / 30) * Math.PI * 2;
          const r = 80;
          const x = Math.cos(angle) * r;
          const y = Math.sin(angle) * r;
          
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="4"
              fill={i % 3 === 0 ? brand.aqua : i % 3 === 1 ? brand.violet : brand.amber}
              opacity="0.7"
            />
          );
        })}
      </g>

      {/* Pointillist note */}
      <text x="397" y="1020" textAnchor="middle" fontFamily="serif" fontSize="11" fill={brand.aqua} opacity="0.5">
        divisionnisme optique
      </text>
    </g>
  );
}
